import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { getProjects, getCustomers, createProject, deleteProject } from '../services/api';
import { toast } from 'sonner';
import { 
  FolderOpen, 
  Plus, 
  Search, 
  FileText,
  Trash2,
  ArrowRight,
  Loader2,
  Building2,
  Filter
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ProjectsPage = () => {
  const { canCreate, canDelete } = useAuth();
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customer_id: '',
    integration_type: '',
    tags: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, customersRes] = await Promise.all([
        getProjects(),
        getCustomers()
      ]);
      setProjects(projectsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    setCreating(true);
    try {
      const tags = formData.tags.split(',').map(t => t.trim()).filter(Boolean);
      await createProject({ ...formData, tags });
      toast.success('Project created successfully');
      setShowCreateDialog(false);
      setFormData({ name: '', description: '', customer_id: '', integration_type: '', tags: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete project "${name}" and all related documentation?`)) return;
    try {
      await deleteProject(id);
      toast.success('Project deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.customer_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCustomer = filterCustomer === 'all' || p.customer_id === filterCustomer;
    return matchesSearch && matchesCustomer;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="projects-title">
              Projects
            </h1>
            <p className="text-slate-500 mt-1">Manage integration projects across all customers</p>
          </div>
          {canCreate && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#2B8FD8] hover:bg-[#2075b3]" data-testid="add-project-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Project
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Plus_Jakarta_Sans']">Create New Project</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Customer *</Label>
                  <Select 
                    value={formData.customer_id} 
                    onValueChange={(v) => setFormData({ ...formData, customer_id: v })}
                  >
                    <SelectTrigger data-testid="project-customer-select">
                      <SelectValue placeholder="Select a customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Project Name *</Label>
                  <Input
                    data-testid="project-name-input"
                    placeholder="SAP B1 Integration"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    data-testid="project-description-input"
                    placeholder="Integration project description..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Integration Type</Label>
                  <Input
                    data-testid="project-type-input"
                    placeholder="B1IF, SQL, API, etc."
                    value={formData.integration_type}
                    onChange={(e) => setFormData({ ...formData, integration_type: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tags (comma-separated)</Label>
                  <Input
                    data-testid="project-tags-input"
                    placeholder="sap, inventory, sync"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#2B8FD8] hover:bg-[#2075b3]"
                    disabled={creating}
                    data-testid="create-project-submit"
                  >
                    {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Create Project
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search projects..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-projects-input"
            />
          </div>
          <Select value={filterCustomer} onValueChange={setFilterCustomer}>
            <SelectTrigger className="w-[200px]" data-testid="filter-customer-select">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Projects Grid */}
        {filteredProjects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Card 
                key={project.id} 
                className="border-slate-200 hover:shadow-md transition-all hover:border-blue-200 group"
                data-testid={`project-card-${project.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-50 rounded-lg">
                        <FolderOpen className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">
                          {project.name}
                        </CardTitle>
                        <Link 
                          to={`/customers/${project.customer_id}`}
                          className="text-sm text-[#2B8FD8] hover:underline flex items-center gap-1"
                        >
                          <Building2 className="h-3 w-3" />
                          {project.customer_name}
                        </Link>
                      </div>
                    </div>
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                        onClick={(e) => { e.preventDefault(); handleDelete(project.id, project.name); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {project.integration_type && (
                    <Badge variant="outline" className="mb-3">
                      {project.integration_type}
                    </Badge>
                  )}
                  {project.description && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{project.description}</p>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <FileText className="h-4 w-4" />
                      {project.documentation_count} docs
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {project.tags && project.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {project.tags.slice(0, 3).map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-slate-100 text-slate-600 text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <Link to={`/projects/${project.id}`}>
                    <Button variant="outline" size="sm" className="w-full group-hover:border-[#2B8FD8] group-hover:text-[#2B8FD8]">
                      View Project <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200 border-dashed">
            <CardContent className="py-16 text-center">
              <FolderOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Plus_Jakarta_Sans']">
                {searchQuery || filterCustomer !== 'all' ? 'No projects found' : 'No projects yet'}
              </h3>
              <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                {searchQuery || filterCustomer !== 'all' 
                  ? 'Try adjusting your search or filter' 
                  : customers.length === 0 
                    ? 'Create a customer first, then add projects'
                    : 'Get started by creating your first project'}
              </p>
              {customers.length > 0 && !searchQuery && filterCustomer === 'all' && (
                <Button 
                  className="bg-[#2B8FD8] hover:bg-[#2075b3]"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Project
                </Button>
              )}
              {customers.length === 0 && (
                <Link to="/customers">
                  <Button className="bg-[#2B8FD8] hover:bg-[#2075b3]">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Customer First
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ProjectsPage;
